import { useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Table } from 'antd';
import type { OAuthAccountOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import { OAuthProviderTag } from '@/components/iam/tags';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const UserOAuthAccountsTab = ({ userId }: { userId: number }) => {
  const t = useTranslate();
  const { tableProps, sorters, result } = useTable<OAuthAccountOut>({
    resource: 'iam.oauth_accounts',
    filters: {
      permanent: [{ field: 'user_id', operator: 'eq', value: userId }],
    },
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  return (
    <Card
      title={`${t('iam.users.tabs.oauth_accounts')} (${result?.total ?? 0})`}
    >
      <Table<OAuthAccountOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<OAuthAccountOut>
          dataIndex="id"
          title={t('base.fields.id')}
          width={90}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowIconLink resource="iam.oauth_accounts" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<OAuthAccountOut>
          dataIndex="oauth_name"
          title={t('iam.oauth_accounts.fields.oauth_name')}
          render={(value: string) => <OAuthProviderTag provider={value} />}
        />
        <Table.Column<OAuthAccountOut>
          dataIndex="account_id"
          title={t('iam.oauth_accounts.fields.account_id')}
          render={(value: string) => orDash(value)}
        />
        <Table.Column<OAuthAccountOut>
          dataIndex="account_email"
          title={t('iam.oauth_accounts.fields.account_email')}
          render={(value: string) => orDash(value)}
        />
        <Table.Column<OAuthAccountOut>
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string) => orDash(formatDateTime(value))}
        />
      </Table>
    </Card>
  );
};
